import type { PidAnswers } from './IdvContext';
import IdVerificationContext, { DEFAULT_OPTIONS } from './IdvContext';
import { useAsyncFn, useLatest } from 'react-use';
import { useRef, useState } from 'react';
import type { LogData, LogLevel } from '../../models';
import { ExitReason, TermSet } from '../../models';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@zg-rentals/react-query5';
import type { EmitMetric, TrackEvent } from './clickstream/events';
import { DatarouterError } from '@zg-rentals/web-base';
import type { IdentityCheckQuestion } from '@zg-rentals/ts-id-verification-client';
import { formatPidAnswersForReview } from './format/format';
import { WizardProvider } from './wizard/WizardContext';
import { WizardContent } from './components/WizardContent';
import { LoadPassiveAuth } from './LoadPassiveAuth';

function noop() {}

export const LANDLORD_METRIC_PREFIX = 'IdvLandlord';
export const RENTER_METRIC_PREFIX = 'IdvRenter';

/**
 * For a function that may be sync or async, returns the function loading state
 * and a ref with the latest function instance
 */
function useLatestAsyncFn<T extends (...args: Array<never>) => unknown>(fn: T) {
  const [{ loading }, callback] = useAsyncFn(async (...args) => await fn(...(args as Array<never>)), [fn]);
  return [loading, useLatest(callback as unknown as T)] as const;
}

interface IdVerificationProps {
  id: string;
  termSet?: TermSet;
  pidAnswersForReview?: Omit<PidAnswers, 'dob' | 'ssn'>;
  skipLandingStep?: boolean;
  handleScreeningReports?: boolean;
  reauthenticate?: boolean;
  onSubmitPidForm?: (answers: PidAnswers) => void;
  onExit: (exitReason: { reason: ExitReason; success: boolean }) => void | Promise<void>;
  trackEvent?: TrackEvent;
  emitMetric?: EmitMetric;
  log?: (logLevel: LogLevel, logData: LogData) => void;
}

export default function Idv({
  id = DEFAULT_OPTIONS.id,
  termSet = TermSet.RenterScreening,
  pidAnswersForReview = {
    firstName: '',
    middleName: '',
    lastName: '',
    currentAddress: '',
    currentCity: '',
    currentState: '',
    currentZip: '',
    phone: '',
  },
  skipLandingStep = false,
  handleScreeningReports = false,
  reauthenticate = false,
  onSubmitPidForm = noop,
  onExit = noop,
  trackEvent = noop,
  emitMetric = noop,
  log = noop,
}: IdVerificationProps) {
  const latestOnSubmitPidForm = useLatest(onSubmitPidForm);
  const [isOnExitLoading, latestOnExit] = useLatestAsyncFn(onExit);
  const latestTrackEvent = useLatest(trackEvent);
  const latestEmitMetric = useLatest(emitMetric);
  const latestLog = useLatest(log);
  const [kiqQuestionSet, setKiqQuestionSet] = useState<Array<IdentityCheckQuestion>>([]);
  const [pidAnswers, setPidAnswers] = useState({
    ...formatPidAnswersForReview(pidAnswersForReview),
  });
  const [pidRemainingAttempts, setPidRemainingAttempts] = useState<number | null>(null);
  const [reauthenticating] = useState(reauthenticate);
  const [hasInitializedWizard, setHasInitializedWizard] = useState(false);

  const queryClient = useRef(
    new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          refetchOnWindowFocus: false,
        },
      },
      queryCache: new QueryCache({
        onError: async (error) => {
          if (DatarouterError.is(error) && error.responseStatus === 401) {
            await latestOnExit.current({ reason: ExitReason.NotAuthenticated, success: false });
          }
        },
      }),
      mutationCache: new MutationCache({
        onError: async (error) => {
          if (DatarouterError.is(error) && error.responseStatus === 401) {
            await latestOnExit.current({ reason: ExitReason.NotAuthenticated, success: false });
          }
        },
      }),
    }),
  );

  return (
    <IdVerificationContext.Provider
      value={{
        ...DEFAULT_OPTIONS,
        termSet,
        id,
        isOnExitLoading,
        latestOnExit,
        latestOnSubmitPidForm,
        latestTrackEvent,
        latestEmitMetric,
        latestLog,
        skipLandingStep,
        handleScreeningReports,
        reauthenticating,
        hasInitializedWizard,
        setHasInitializedWizard,
        pidAnswers,
        setPidAnswers,
        // TODO: refactor kiqQuestionSet to be pulled from the
        // react-query cache and remove from context
        kiqQuestionSet,
        setKiqQuestionSet,
        pidRemainingAttempts,
        setPidRemainingAttempts,
      }}
    >
      <QueryClientProvider client={queryClient.current}>
        <WizardProvider>
          <LoadPassiveAuth />
          <WizardContent />
        </WizardProvider>
      </QueryClientProvider>
    </IdVerificationContext.Provider>
  );
}
